'use client';

import { useState, useEffect } from 'react';
import { MessageCircle, X, Send, CheckCircle2, Loader2, Calendar } from 'lucide-react';
import { trackLeadFormSubmission, trackCtaClick, trackScheduleMeetingClick, trackFormStart } from '@/lib/analytics';
import { setLeadSubmittedCookie } from '@/lib/utils/lead-cookie';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

interface FloatingHelpButtonProps {
  /** Page type for tracking */
  pageType: 'kb_article' | 'tool_page' | 'blog_post';
  /** Current page slug for tracking */
  pageSlug: string;
  /** Category for tracking */
  category?: string;
  /** Scroll threshold before showing (default: 400px) */
  scrollThreshold?: number;
}

interface FormData {
  email: string;
  name: string;
  message: string;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const SESSION_STORAGE_KEY = 'floating-help-dismissed';

/**
 * Floating "Need Help?" button for desktop users.
 * Shows after scrolling, expands to reveal a mini contact form.
 * Dismissible with session memory.
 */
export default function FloatingHelpButton({
  pageType,
  pageSlug,
  category,
  scrollThreshold = 400,
}: FloatingHelpButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [formData, setFormData] = useState<FormData>({ email: '', name: '', message: '' });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Track form start on first field focus
  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Floating Help', pageType);
    }
  };

  // Check session storage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (dismissed === 'true') {
        setIsDismissed(true);
      }
    }
  }, []);

  // Handle scroll visibility
  useEffect(() => {
    const handleScroll = () => {
      if (isDismissed) return;

      const scrollY = window.scrollY;
      const shouldShow = scrollY > scrollThreshold;

      // Hide when near bottom (where main CTA typically is)
      const nearBottom = window.innerHeight + scrollY >= (document.body?.offsetHeight ?? document.documentElement.scrollHeight) - 500;

      setIsVisible(shouldShow && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollThreshold, isDismissed]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    setIsExpanded(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  };

  const handleToggle = () => {
    if (!isExpanded) {
      trackCtaClick('Floating Help: Open', 'inline', '');
    }
    setIsExpanded(!isExpanded);
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) {
      setStatus('success');
      return;
    }

    if (!formData.email.trim()) {
      setErrorMessage('Email is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setErrorMessage('Please enter a valid email');
      return;
    }

    // Block non-English (non-Latin script) input in free-text fields
    if (containsNonLatinText(formData.name) || containsNonLatinText(formData.message)) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          message: formData.message || `Quick help request from ${pageType}`,
          formName: 'Floating Help - Quick Contact',
          src: 'floating-help',
          id: pageSlug,
          cat: category,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit');
      }

      // Track conversion in GA4 and Clarity (includes identification)
      trackLeadFormSubmission('Floating Help - Quick Contact', pageType, undefined, formData.email);

      setStatus('success');

      // Set cookie to suppress exit intent for 7 days
      setLeadSubmittedCookie();
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Warn immediately if the user types non-English (non-Latin script) characters
    if (name !== 'email' && containsNonLatinText(value)) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      return;
    }

    if (errorMessage) setErrorMessage('');
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div
      className="hidden lg:block fixed bottom-6 right-6 z-50"
      style={{ animation: 'slideInRight 0.3s ease-out' }}
    >
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Expanded Form */}
      {isExpanded && (
        <div className="mb-3 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-80 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-3 flex items-center justify-between">
            <span className="font-semibold">Quick Help Request</span>
            <button
              onClick={() => setIsExpanded(false)}
              className="p-1 hover:bg-white dark:hover:bg-gray-800/20 rounded transition-colors"
              aria-label="Minimize"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-4">
            {status === 'success' ? (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-full mb-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Thank You!</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">We&apos;ll be in touch the same business day.</p>
                <a
                  href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackScheduleMeetingClick('floating_help_success')}
                  className="inline-flex items-center gap-2 text-sm text-[#2813e8] hover:underline font-medium"
                >
                  <Calendar className="w-4 h-4" />
                  Or schedule a call now
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} name="Floating Help - Quick Contact" className="space-y-3">
                {/* Honeypot */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    aria-label="Leave blank"
                  />
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    aria-label="Email address"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={handleFormFocus}
                    placeholder="Email address *"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                    required
                  />
                </div>

                {/* Name (optional) */}
                <div>
                  <input
                    type="text"
                    name="name"
                    aria-label="Name (optional)"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={handleFormFocus}
                    placeholder="Name (optional)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                  />
                </div>

                {/* Message (optional) */}
                <div>
                  <textarea
                    name="message"
                    aria-label="How can we help? (optional)"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={handleFormFocus}
                    placeholder="How can we help? (optional)"
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent resize-none"
                  />
                </div>

                {/* Error message */}
                {errorMessage && (
                  <p className="text-xs text-red-600 dark:text-red-400">{errorMessage}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-2 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Request
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  We respect your privacy. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Main Button */}
      <div className="relative group">
        <button
          onClick={handleToggle}
          className={`flex items-center gap-2 px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${
            isExpanded
              ? 'bg-gray-800 text-white'
              : 'bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white'
          }`}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-semibold">{isExpanded ? 'Minimize' : 'Need Help?'}</span>
        </button>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-700"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
